import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SearchHistoryService {
  private readonly STORAGE_KEY = 'globalSearchHistory';
  private readonly MAX_HISTORY_ITEMS = 15; // Limit the number of stored items

  private historySubject: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  public history$: Observable<string[]> = this.historySubject.asObservable();

  constructor() {
    this.loadHistoryFromStorage();
  }

  private loadHistoryFromStorage(): void {
    try {
      const storedHistory = localStorage.getItem(this.STORAGE_KEY);
      if (storedHistory) {
        const parsedHistory = JSON.parse(storedHistory);
        if (Array.isArray(parsedHistory)) {
          // Ensure all items are strings, just in case storage got corrupted
          const validatedHistory = parsedHistory.filter(item => typeof item === 'string');
          this.historySubject.next(validatedHistory);
        }
      }
    } catch (error) {
      console.error('Error loading search history from localStorage:', error);
      localStorage.removeItem(this.STORAGE_KEY); // Clear corrupted storage
      this.historySubject.next([]);
    }
  }

  addSearchTerm(term: string): void {
    const trimmedTerm = term.trim();
    if (!trimmedTerm) {
      return; // Don't add empty or whitespace-only terms
    }

    const currentHistory = this.historySubject.getValue();
    
    // Remove the term if it already exists to move it to the front
    const filteredHistory = currentHistory.filter(item => item.toLowerCase() !== trimmedTerm.toLowerCase());

    // Add the new term to the beginning
    const updatedHistory = [trimmedTerm, ...filteredHistory];

    // Limit the history size
    const limitedHistory = updatedHistory.slice(0, this.MAX_HISTORY_ITEMS);

    // Update the BehaviorSubject and localStorage
    this.historySubject.next(limitedHistory);
    this.saveHistoryToStorage(limitedHistory);
  }

  private saveHistoryToStorage(history: string[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(history));
    } catch (error) {
      console.error('Error saving search history to localStorage:', error);
    }
  }

  // Optional: Method to clear history
  clearHistory(): void {
     this.historySubject.next([]);
     localStorage.removeItem(this.STORAGE_KEY);
  }
} 